import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Stack,
  FormControl,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  AutoAwesome as AutoAwesomeIcon,
  Psychology as PsychologyIcon,
  Tune as TuneIcon,
  Save as SaveIcon,
  Restore as RestoreIcon
} from '@mui/icons-material';
import { useAuth } from '../../../contexts/AuthContext';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useApp } from '../../../contexts/AppContext';
import { getAppSettings, updateAppSettings } from '../../../firebase/services/appSettingsService';
import BaseButton from '../../../components/common/BaseButton';

const SummarySettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  const { currentUser } = useAuth();
  const { addSystemNotification } = useNotifications();
  const { showAlert } = useApp();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isFirefox = typeof navigator !== 'undefined' && navigator.userAgent.toLowerCase().includes('firefox');

  // Configuration du style moderne sobre
  const glassBoxSx = {
    backgroundColor: 'white',
    borderRadius: '12px',
    border: '1px solid rgba(0, 0, 0, 0.08)',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      boxShadow: '0 6px 24px rgba(0, 0, 0, 0.12)',
    }
  };

  const sectionHeaderSx = {
    fontWeight: 700,
    color: '#1f2937',
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    mb: 3,
    fontSize: '1.2rem'
  };

  // Charger les paramètres au montage
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const appSettings = await getAppSettings();
      setSettings({
        model: appSettings?.openAISettings?.model || 'gpt-4o'
      });
    } catch (error) {
      addSystemNotification('Erreur lors du chargement des paramètres:', 'error');
      setError('Impossible de charger les paramètres de résumé');
    } finally {
      setLoading(false);
    }
  };

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
    setHasChanges(true);
    setSuccess(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    
    try {
      // Récupérer les paramètres existants
      const currentSettings = await getAppSettings();
      
      // Mettre à jour uniquement les paramètres OpenAI
      const updatedSettings = {
        ...currentSettings,
        openAISettings: {
          ...currentSettings.openAISettings,
          model: settings.model
        }
      };
      
      await updateAppSettings(updatedSettings);
      
      setSuccess(true);
      setHasChanges(false);
      showAlert?.('Paramètres de résumé sauvegardés avec succès !', 'success');
      
      // Masquer le message de succès après 3 secondes
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      addSystemNotification('Erreur lors de la sauvegarde:', 'error');
      setError('Erreur lors de la sauvegarde des paramètres');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const defaultSettings = {
        model: 'gpt-4o'
      };
      
      setSettings(defaultSettings);
      setHasChanges(true);
    } catch (error) {
      addSystemNotification("Erreur lors de la réinitialisation:", 'error');
      setError('Erreur lors de la réinitialisation des paramètres');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !settings) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: 400,
        ...glassBoxSx,
        m: 3
      }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress sx={{ mb: 2, color: '#3F51B5' }} />
          <Typography variant="h6" sx={{ color: '#374151', fontWeight: 600 }}>
            Chargement des paramètres...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* En-tête */}
      <Box sx={{ 
        ...glassBoxSx,
        mb: 3,
        p: 3,
        backgroundColor: '#f8fafc',
        border: '1px solid rgba(156, 39, 176, 0.1)',
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Box sx={{
                p: 1.5,
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #9C27B0 0%, #BA68C8 100%)',
                boxShadow: '0 6px 20px rgba(156, 39, 176, 0.3)',
              }}>
                <AutoAwesomeIcon sx={{ fontSize: 28, color: 'white' }} />
              </Box>
              <Box>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 700,
                    color: '#1f2937',
                    mb: 0.5
                  }}
                >
                  🤖 Paramètres de Résumé IA
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280' }}>
                  Configurez l'intelligence artificielle pour générer des résumés pertinents
                </Typography>
              </Box>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Tooltip title="Réinitialiser aux valeurs par défaut">
              <IconButton 
                onClick={handleReset}
                disabled={loading || saving}
                sx={{
                  background: 'rgba(255,255,255,0.15)',
                  backdropFilter: 'blur(8px)',
                  color: '#9C27B0',
                  '&:hover': {
                    background: 'rgba(255,255,255,0.25)',
                    transform: 'scale(1.05)'
                  }
                }}
              >
                <RestoreIcon />
              </IconButton>
            </Tooltip>
            
            <BaseButton
              onClick={handleSave}
              disabled={!hasChanges || saving}
              loading={saving}
              startIcon={!saving ? <SaveIcon /> : undefined}
              variant="contained"
              sx={{
                background: hasChanges 
                  ? 'linear-gradient(135deg, #9C27B0 0%, #BA68C8 100%)'
                  : 'rgba(156, 39, 176, 0.3)',
                color: 'white',
                '&:hover': {
                  background: hasChanges 
                    ? 'linear-gradient(135deg, #8E24AA 0%, #AB47BC 100%)'
                    : 'rgba(156, 39, 176, 0.4)',
                },
                '&:disabled': {
                  background: 'rgba(156, 39, 176, 0.2)',
                  color: 'rgba(255,255,255,0.5)'
                }
              }}
            >
              {saving ? 'Sauvegarde...' : 'Sauvegarder'}
            </BaseButton>
          </Box>
        </Box>
      </Box>

      {/* Messages d'état */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3, 
            borderRadius: '12px',
            backgroundColor: 'white',
            border: '1px solid rgba(239, 68, 68, 0.2)',
            boxShadow: '0 4px 16px rgba(239, 68, 68, 0.08)'
          }}
        >
          ❌ {error}
        </Alert>
      )}

      {success && (
        <Alert 
          severity="success" 
          sx={{ 
            mb: 3, 
            borderRadius: '12px',
            backgroundColor: 'white',
            border: '1px solid rgba(34, 197, 94, 0.2)',
            boxShadow: '0 4px 16px rgba(34, 197, 94, 0.08)'
          }}
        >
          ✅ Paramètres sauvegardés avec succès !
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Sélection du modèle */}
        <Grid item xs={12} md={8}>
          <Box sx={glassBoxSx} p={2}>
            <Typography variant="h6" sx={sectionHeaderSx}>
              <AutoAwesomeIcon />
              Modèle d'Intelligence Artificielle
            </Typography>

            <Stack spacing={3}>
              <Box sx={{ p: 2 }}>
                <Typography variant="body1" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  🤖 Choisissez votre modèle IA
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={settings?.model || 'gpt-4o'}
                    onChange={(e) => handleSettingChange('model', e.target.value)}
                    sx={{ 
                      borderRadius: '12px',
                      backgroundColor: '#f8fafc',
                      border: '1px solid rgba(0, 0, 0, 0.08)',
                      '&:hover': {
                        backgroundColor: 'white',
                        borderColor: 'rgba(63, 81, 181, 0.3)',
                      },
                      '&.Mui-focused': {
                        backgroundColor: 'white',
                        borderColor: '#3F51B5',
                        boxShadow: '0 4px 12px rgba(63, 81, 181, 0.15)',
                      }
                    }}
                  >
                    <MenuItem value="gpt-4.1">
                      <Box>
                        <Typography variant="body2" fontWeight={600}>GPT-4.1</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Modèle le plus récent et performant (2025) - Contexte très long
                        </Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="gpt-4.1-mini">
                      <Box>
                        <Typography variant="body2" fontWeight={600}>GPT-4.1 Mini</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Performance GPT-4o avec latence réduite (-50%) et coût optimisé (-83%)
                        </Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="gpt-4.1-nano">
                      <Box>
                        <Typography variant="body2" fontWeight={600}>GPT-4.1 Nano</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Ultra-rapide et économique - Idéal pour classification et tâches simples
                        </Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="gpt-4o">
                      <Box>
                        <Typography variant="body2" fontWeight={600}>GPT-4o</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Optimisé pour la rapidité et l'efficacité - Idéal usage courant
                        </Typography>
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Stack>
          </Box>
        </Grid>

        {/* Conseils d'utilisation */}
        <Grid item xs={12}>
          <Box sx={glassBoxSx} p={2}>
            <Typography variant="h6" sx={sectionHeaderSx}>
              <TuneIcon />
              Conseils d'utilisation
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Box sx={{
                  p: 2.5,
                  borderRadius: '12px',
                  backgroundColor: '#f0fdf4',
                  border: '1px solid rgba(34, 197, 94, 0.2)',
                }}>
                  <Typography variant="body1" fontWeight={600} sx={{ color: '#16a34a', mb: 2 }}>
                    ✅ Usage quotidien recommandé
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1f2937', fontWeight: 600, mb: 1 }}>
                    GPT-4.1 Mini & GPT-4o
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6b7280', lineHeight: 1.5 }}>
                    • Réunions standard (1-2 heures)
                    <br />• Excellent rapport qualité/prix
                    <br />• Latence réduite pour 4.1 Mini
                    <br />• Performance fiable et rapide
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{
                  p: 2.5,
                  borderRadius: '12px',
                  backgroundColor: '#fef3c7',
                  border: '1px solid rgba(245, 158, 11, 0.2)',
                }}>
                  <Typography variant="body1" fontWeight={600} sx={{ color: '#d97706', mb: 2 }}>
                    ⚡ Ultra-rapide et économique
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1f2937', fontWeight: 600, mb: 1 }}>
                    GPT-4.1 Nano
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6b7280', lineHeight: 1.5 }}>
                    • Réunions courtes (moins de 1h)
                    <br />• Classification et synthèse simple
                    <br />• Budget très serré
                    <br />• Vitesse maximale prioritaire
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12}>
                <Box sx={{
                  p: 2.5,
                  borderRadius: '12px',
                  backgroundColor: '#fdf4ff',
                  border: '1px solid rgba(156, 39, 176, 0.2)',
                }}>
                  <Typography variant="body1" fontWeight={600} sx={{ color: '#9333ea', mb: 2 }}>
                    🚀 Pour les cas les plus complexes
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1f2937', fontWeight: 600, mb: 1 }}>
                    GPT-4.1
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6b7280', lineHeight: 1.5 }}>
                    • Réunions très longues (3+ heures) • Sujets techniques très complexes • Analyses multi-thématiques approfondies • Contexte de 1M tokens nécessaire
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SummarySettings; 